import { MemberTableSubject } from "@components/common/faro-table/faro-table-types";
import { RoleChangeSuccessDescription } from "@components/table/members/members-column/role-column/role-change-success-description";
import { RolePartiallyChanged } from "@components/table/members/members-column/role-column/role-partially-changed";
import { RoleChangeSubject } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { useToast } from "@flat-ui";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateMemberRoleInGroup } from "@store/groups/groups-slice-thunk";
import { updateMemberRoleInWorkspace } from "@store/members/members-slice-thunk";
import { updateMemberRoleInProjectSingle } from "@store/projects/projects-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { formatUserRoleType } from "@utils/data-display";
import {
  GroupMemberEvents,
  ProjectMemberEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface UseChangeMemberRoleParams {
  /** Company ID */
  companyId: string;

  /** Location from which the member list is rendered, for tracking purposes. */
  location: string;

  /** Context in which the role change is happening */
  subjectType: MemberTableSubject;

  /** Identity of the member whose role is being changed */
  memberIdentity: string;

  /** Current role of the member (to skip no-op changes) */
  currentRole: string;

  /** The selected project (for project member tables) */
  selectedProject?: SdbProject | null;

  /** The selected group (for group member tables) */
  group?: FaroCoreApiTypes.GroupDetailResponse | null;
}

interface UseChangeMemberRoleReturn {
  /** Dispatches the role change and shows a toast notification */
  changeMemberRole: (newRole: string) => Promise<void>;
}

/**
 * Hook for changing a single member's role.
 * Dispatches the appropriate thunk based on subject type and shows toast on success.
 * Reusable across different UI patterns (table cell dropdown, 3-dot menu, etc.).
 */
export function useChangeMemberRole({
  companyId,
  subjectType,
  memberIdentity,
  currentRole,
  selectedProject,
  group,
  location,
}: UseChangeMemberRoleParams): UseChangeMemberRoleReturn {
  const dispatch = useAppDispatch();
  const { openToast } = useToast();
  const { trackEvent } = useTrackEvent();

  /** Dispatches the role change for the member and shows a toast notification */
  async function changeMemberRole(newRole: string): Promise<void> {
    if (newRole === currentRole) {
      return;
    }

    if (subjectType === "project" && selectedProject) {
      await changeProjectRole(newRole as FaroCoreApiTypes.ProjectRole);
      return;
    }

    if (subjectType === "group" && group) {
      await changeGroupRole(newRole as FaroCoreApiTypes.GroupRole);
      return;
    }

    if (subjectType === "workspace" || subjectType === "team") {
      await changeCompanyRole(
        newRole as FaroCoreApiTypes.UpdateCompanyMemberRole
      );
    }
  }

  async function changeProjectRole(
    role: FaroCoreApiTypes.ProjectRole
  ): Promise<void> {
    trackEvent({
      name: ProjectMemberEvents.changeRole,
      props: { role, location },
    });

    await dispatch(
      updateMemberRoleInProjectSingle({
        companyId,
        projectId: selectedProject!.id,
        userIdentity: memberIdentity,
        payload: { role },
      })
    ).unwrap();

    openToast({
      title: "Role Changed",
      message: <RoleChangeSuccessDescription role={formatUserRoleType(role)} />,
    });
  }

  async function changeGroupRole(
    role: FaroCoreApiTypes.GroupRole
  ): Promise<void> {
    trackEvent({
      name: GroupMemberEvents.changeRole,
      props: { role, location },
    });

    await dispatch(
      updateMemberRoleInGroup({
        companyId,
        groupId: group!.id,
        role,
        userIdentity: memberIdentity,
      })
    ).unwrap();

    openToast({
      title: "Role Changed",
      message: <RoleChangeSuccessDescription role={formatUserRoleType(role)} />,
    });
  }

  async function changeCompanyRole(
    role: FaroCoreApiTypes.UpdateCompanyMemberRole
  ): Promise<void> {
    const result = await dispatch(
      updateMemberRoleInWorkspace({
        companyId,
        role,
        identity: memberIdentity,
      })
    ).unwrap();

    // Company role change may result in a different role than intended
    // (e.g., group/project managers can't be downgraded to member,
    //  or the user belongs to a team that grants a higher effective role)
    if (result.role !== role) {
      const newRoleName = formatUserRoleType(result.role);
      // GM/PM are role types that come from being a manager of a group/project.
      // Any other higher-than-requested role can only originate from a team assignment.
      const roleSubject: RoleChangeSubject =
        result.role === "COMPANY_MANAGER" || result.role === "PROJECT_MANAGER"
          ? "group"
          : "team";

      openToast({
        title: (
          <>
            Role Changed to <strong>{newRoleName}</strong>
          </>
        ),
        message: (
          <RolePartiallyChanged
            intendedRole={formatUserRoleType(role)}
            newRole={newRoleName}
            roleSubject={roleSubject}
          />
        ),
        variant: "warning",
      });
    } else {
      openToast({
        title: "Role Changed",
        message: (
          <RoleChangeSuccessDescription role={formatUserRoleType(role)} />
        ),
      });
    }
  }

  return { changeMemberRole };
}
